/**
 * Report Parser - Parse markdown analysis report into structured JSON/CSV
 * SLAM Sports AI - College Recruiting System
 *
 * @module reportParser
 */

/**
 * @typedef {'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW' | 'NONE'} RiskLevel
 */

/**
 * @typedef {Object} TellDetection
 * @property {string} id - Unique identifier
 * @property {string} bodyPart - Body part associated with tell
 * @property {string} tellType - Type of tell detected
 * @property {string} description - Description of the tell
 * @property {number} confidence - Confidence percentage
 * @property {'HIGH' | 'MEDIUM' | 'LOW'} confidenceLevel - Confidence level category
 * @property {string[]} pitchTypesAffected - Pitch types affected by this tell
 * @property {string} recommendation - Recommended action
 */

/**
 * @typedef {Object} PitchAnalysis
 * @property {number} pitchNumber - Pitch number in sequence
 * @property {string} pitchType - Type of pitch
 * @property {string[]} indicators - Detected indicators
 * @property {number} confidence - Confidence percentage
 */

/**
 * @typedef {Object} MediaItemExport
 * @property {string} filename - Media filename
 * @property {string} type - Media type
 * @property {Object} [metadata] - Optional metadata
 */

/**
 * @typedef {Object} AnalysisExportJSON
 * @property {Object} metadata - Export metadata
 * @property {Object} summary - Analysis summary
 * @property {TellDetection[]} tells - Detected tells
 * @property {PitchAnalysis[]} pitchByPitch - Pitch-by-pitch analysis
 * @property {string[]} recommendations - Recommendations
 * @property {MediaItemExport[]} media - Media items
 * @property {string} rawMarkdown - Raw markdown content
 */

/**
 * Parse markdown report into structured JSON export format
 * @param {string} markdown - The markdown content to parse
 * @param {Array<{name: string, type: string, metadata?: {pitchType?: string, cameraAngle?: string}}>} mediaItems - Media items
 * @returns {AnalysisExportJSON} Parsed export data
 */
export function parseReportToJSON(markdown, mediaItems) {
  // Extract risk level from markdown
  const riskLevel = extractRiskLevel(markdown);

  // Extract tells with confidence
  const tells = extractTells(markdown);

  // Extract pitch-by-pitch analysis
  const pitchByPitch = extractPitchAnalysis(markdown);

  // Extract recommendations
  const recommendations = extractRecommendations(markdown);

  // Calculate overall confidence
  const overallConfidence = calculateOverallConfidence(tells);

  return {
    metadata: {
      exportTimestamp: new Date().toISOString(),
      analysisTimestamp: new Date().toISOString(),
      version: '1.0.0',
      generatedBy: 'SLAM Sports AI Analyzer - THE WIRE',
    },
    summary: {
      overallRiskLevel: riskLevel,
      overallConfidence,
      totalPitchesAnalyzed: pitchByPitch.length || countPitchReferences(markdown),
      tellsDetected: tells.length,
      primaryTell: tells[0]?.tellType,
    },
    tells,
    pitchByPitch,
    recommendations,
    media: mediaItems.map((m) => ({
      filename: m.name,
      type: m.type,
      metadata: m.metadata,
    })),
    rawMarkdown: markdown,
  };
}

/**
 * Extract overall risk level from markdown
 * @param {string} markdown - Markdown content
 * @returns {RiskLevel} Risk level
 */
function extractRiskLevel(markdown) {
  // Look for patterns like "Overall Tipping Risk: HIGH" or "Risk Level: CRITICAL"
  const patterns = [
    /(?:Overall\s+)?(?:Tipping\s+)?Risk(?:\s+Level)?[:\s]*\**(CRITICAL|HIGH|MEDIUM|LOW|NONE)\**/i,
    /Risk[:\s]*\**(CRITICAL|HIGH|MEDIUM|LOW|NONE)\**/i,
    /\*\*(CRITICAL|HIGH|MEDIUM|LOW|NONE)\*\*\s*(?:risk|tipping)/i,
  ];

  for (const pattern of patterns) {
    const match = markdown.match(pattern);
    if (match) {
      return normalizeRiskLevel(match[1]);
    }
  }

  // If tells are detected, estimate risk
  const tellCount = (markdown.match(/confidence|tell|indicator/gi) || []).length;
  if (tellCount > 10) return 'HIGH';
  if (tellCount > 5) return 'MEDIUM';
  if (tellCount > 0) return 'LOW';
  return 'NONE';
}

/**
 * Normalize risk level string
 * @param {string} level - Risk level string
 * @returns {RiskLevel} Normalized risk level
 */
function normalizeRiskLevel(level) {
  const normalized = level?.toUpperCase().trim();
  if (['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'NONE'].includes(normalized)) {
    return /** @type {RiskLevel} */ (normalized);
  }
  return 'MEDIUM';
}

/**
 * Extract tell detections from markdown
 * @param {string} markdown - Markdown content
 * @returns {TellDetection[]} Array of detected tells
 */
function extractTells(markdown) {
  /** @type {TellDetection[]} */
  const tells = [];
  let id = 0;

  // Pattern 1: **Body Part** (X% confidence)
  const pattern1 = /\*\*([^*]+)\*\*[^(]*?\((\d+)%\s*(?:confidence|reliability)\)/gi;
  let match;
  while ((match = pattern1.exec(markdown)) !== null) {
    const confidence = parseInt(match[2]);
    const bodyPart = classifyBodyPart(match[1]);
    const tellType = match[1].trim();

    // Avoid duplicates
    if (!tells.some((t) => t.tellType === tellType)) {
      tells.push({
        id: `tell-${id++}`,
        bodyPart,
        tellType,
        description: extractContextAroundMatch(markdown, match.index, 200),
        confidence,
        confidenceLevel: confidence >= 85 ? 'HIGH' : confidence >= 65 ? 'MEDIUM' : 'LOW',
        pitchTypesAffected: extractPitchTypes(markdown.substring(match.index, match.index + 400)),
        recommendation: findRecommendationForTell(markdown, tellType),
      });
    }
  }

  // Pattern 2: Glove position/Arm slot/etc with percentages
  const bodyPartPatterns = [
    { pattern: /glove\s+position[^.]*?(\d+)%/gi, bodyPart: 'Glove', tellType: 'Glove Position Variance' },
    { pattern: /arm\s+slot[^.]*?(\d+)%/gi, bodyPart: 'Arm', tellType: 'Arm Slot Variance' },
    { pattern: /timing[^.]*?(\d+)%/gi, bodyPart: 'Timing', tellType: 'Timing/Rhythm Variance' },
    { pattern: /grip[^.]*?(\d+)%/gi, bodyPart: 'Grip', tellType: 'Grip Adjustment Visibility' },
    { pattern: /trunk\s+tilt[^.]*?(\d+)%/gi, bodyPart: 'Body', tellType: 'Trunk Tilt Variance' },
    { pattern: /head\s+movement[^.]*?(\d+)%/gi, bodyPart: 'Head', tellType: 'Head Movement' },
  ];

  for (const { pattern, bodyPart, tellType } of bodyPartPatterns) {
    while ((match = pattern.exec(markdown)) !== null) {
      const confidence = parseInt(match[1]);
      if (!tells.some((t) => t.tellType === tellType) && confidence > 0) {
        tells.push({
          id: `tell-${id++}`,
          bodyPart,
          tellType,
          description: extractContextAroundMatch(markdown, match.index, 150),
          confidence,
          confidenceLevel: confidence >= 85 ? 'HIGH' : confidence >= 65 ? 'MEDIUM' : 'LOW',
          pitchTypesAffected: extractPitchTypes(markdown.substring(match.index, match.index + 300)),
          recommendation: findRecommendationForTell(markdown, tellType),
        });
      }
    }
  }

  // Sort by confidence descending
  return tells.sort((a, b) => b.confidence - a.confidence);
}

/**
 * Classify body part from text
 * @param {string} text - Text to classify
 * @returns {string} Body part category
 */
function classifyBodyPart(text) {
  const lower = text.toLowerCase();
  if (lower.includes('glove')) return 'Glove';
  if (lower.includes('arm') || lower.includes('slot')) return 'Arm';
  if (lower.includes('grip')) return 'Grip';
  if (lower.includes('timing') || lower.includes('tempo') || lower.includes('rhythm')) return 'Timing';
  if (lower.includes('trunk') || lower.includes('body') || lower.includes('posture')) return 'Body';
  if (lower.includes('head')) return 'Head';
  if (lower.includes('leg') || lower.includes('lift')) return 'Leg';
  if (lower.includes('set') || lower.includes('stretch')) return 'Set Position';
  return 'Other';
}

/**
 * Extract context around a match
 * @param {string} text - Full text
 * @param {number} index - Match index
 * @param {number} length - Context length
 * @returns {string} Context string
 */
function extractContextAroundMatch(text, index, length) {
  const start = Math.max(0, index - 50);
  const end = Math.min(text.length, index + length);
  let context = text.substring(start, end);

  // Clean up markdown formatting
  context = context.replace(/\*\*/g, '').replace(/\n+/g, ' ').trim();

  // Truncate at sentence boundary if possible
  const lastPeriod = context.lastIndexOf('.');
  if (lastPeriod > length / 2) {
    context = context.substring(0, lastPeriod + 1);
  }

  return context;
}

/**
 * Extract pitch types from text
 * @param {string} text - Text to search
 * @returns {string[]} Array of pitch types
 */
function extractPitchTypes(text) {
  /** @type {string[]} */
  const pitchTypes = [];
  const patterns = [
    { regex: /fastball/gi, name: 'Fastball' },
    { regex: /curveball/gi, name: 'Curveball' },
    { regex: /slider/gi, name: 'Slider' },
    { regex: /changeup|change-up/gi, name: 'Changeup' },
    { regex: /cutter/gi, name: 'Cutter' },
    { regex: /sinker/gi, name: 'Sinker' },
    { regex: /splitter/gi, name: 'Splitter' },
    { regex: /sweeper/gi, name: 'Sweeper' },
  ];

  for (const { regex, name } of patterns) {
    if (regex.test(text) && !pitchTypes.includes(name)) {
      pitchTypes.push(name);
    }
  }

  return pitchTypes;
}

/**
 * Find recommendation for a tell
 * @param {string} markdown - Markdown content
 * @param {string} tellType - Tell type to find recommendation for
 * @returns {string} Recommendation text
 */
function findRecommendationForTell(markdown, tellType) {
  const bodyPartKeyword = tellType.split(' ')[0].toLowerCase();

  // Look for recommendation sections
  const recSectionMatch = markdown.match(
    new RegExp(
      `(?:recommendation|correction|fix)[s]?[^:]*:?[^\\n]*\\n([\\s\\S]*?)(?=\\n##|\\n---|$)`,
      'i'
    )
  );

  if (recSectionMatch) {
    const section = recSectionMatch[1];
    // Find bullet points mentioning the body part
    const lines = section.split('\n');
    for (const line of lines) {
      if (line.toLowerCase().includes(bodyPartKeyword)) {
        return line.replace(/^[-*]\s*/, '').trim();
      }
    }
  }

  return '';
}

/**
 * Extract pitch-by-pitch analysis from markdown tables
 * @param {string} markdown - Markdown content
 * @returns {PitchAnalysis[]} Array of pitch analyses
 */
function extractPitchAnalysis(markdown) {
  /** @type {PitchAnalysis[]} */
  const pitches = [];

  // Look for markdown tables with pitch analysis
  const tableRegex = /\|([^\n]+)\|\n\|[-:\s|]+\|\n((?:\|[^\n]+\|\n?)+)/g;
  let tableMatch;

  while ((tableMatch = tableRegex.exec(markdown)) !== null) {
    const headers = tableMatch[1]
      .split('|')
      .map((h) => h.trim().toLowerCase())
      .filter((h) => h);
    const rows = tableMatch[2].trim().split('\n');

    // Check if this looks like a pitch analysis table
    const isPitchTable =
      headers.some((h) => h.includes('pitch')) ||
      headers.some((h) => h.includes('type')) ||
      headers.some((h) => h.includes('confidence'));

    if (isPitchTable) {
      rows.forEach((row, idx) => {
        const cells = row
          .split('|')
          .map((c) => c.trim())
          .filter((c) => c);
        if (cells.length >= 2) {
          // Try to extract pitch info from cells
          const pitchTypeCell = cells.find((c) =>
            /fastball|curveball|slider|changeup|cutter|sinker|splitter/i.test(c)
          );
          const confidenceCell = cells.find((c) => /\d+%?/.test(c));

          pitches.push({
            pitchNumber: idx + 1,
            pitchType: pitchTypeCell || cells[0] || 'Unknown',
            indicators: cells.slice(1, -1),
            confidence: confidenceCell ? parseInt(confidenceCell.replace('%', '')) : 50,
          });
        }
      });
    }
  }

  return pitches;
}

/**
 * Count pitch references in markdown
 * @param {string} markdown - Markdown content
 * @returns {number} Count of pitch references
 */
function countPitchReferences(markdown) {
  const pitchMentions = markdown.match(/pitch\s+#?\d+|pitch\s+\d+/gi);
  return pitchMentions?.length || 0;
}

/**
 * Extract recommendations from markdown
 * @param {string} markdown - Markdown content
 * @returns {string[]} Array of recommendations
 */
function extractRecommendations(markdown) {
  /** @type {string[]} */
  const recommendations = [];

  // Find recommendation sections (multiple patterns)
  const sectionPatterns = [
    /(?:##\s*)?(?:Recommendation|Correction|Fix|Prioritized\s+Recommendation)[s]?[^:\n]*:?\n([\s\S]*?)(?=\n##|\n---|\n\n\n|$)/gi,
    /(?:coaching_expert|mechanical\s+correction)[^:\n]*:?\n([\s\S]*?)(?=\n##|\n---|$)/gi,
  ];

  for (const pattern of sectionPatterns) {
    let match;
    while ((match = pattern.exec(markdown)) !== null) {
      const section = match[1];

      // Extract bullet points
      const bullets = section.match(/^[-*\d.]\s*(.+)$/gm);
      if (bullets) {
        bullets.forEach((bullet) => {
          const text = bullet.replace(/^[-*\d.]\s*/, '').trim();
          if (text.length > 10 && !recommendations.includes(text)) {
            recommendations.push(text);
          }
        });
      }
    }
  }

  return recommendations;
}

/**
 * Calculate overall confidence from tells
 * @param {TellDetection[]} tells - Array of tells
 * @returns {number} Overall confidence percentage
 */
function calculateOverallConfidence(tells) {
  if (tells.length === 0) return 0;
  const sum = tells.reduce((acc, t) => acc + t.confidence, 0);
  return Math.round(sum / tells.length);
}

/**
 * Convert structured JSON to CSV format
 * @param {AnalysisExportJSON} jsonData - JSON data to convert
 * @returns {string} CSV content
 */
export function convertToCSV(jsonData) {
  /** @type {string[]} */
  const rows = [];

  // Header row
  rows.push('Row Type,Pitch #,Pitch Type,Tell Type,Body Part,Confidence,Description,Recommendation');

  // Summary row
  const summaryDesc = `${jsonData.summary.tellsDetected} tells detected, ${jsonData.summary.totalPitchesAnalyzed} pitches analyzed`;
  rows.push(
    `SUMMARY,0,${jsonData.summary.overallRiskLevel} RISK,Overall Analysis,Overall,${jsonData.summary.overallConfidence},"${escapeCSV(summaryDesc)}",""`
  );

  // Tell detail rows
  jsonData.tells.forEach((tell, idx) => {
    rows.push(
      `TELL,${idx + 1},${tell.pitchTypesAffected.join('/')},${escapeCSV(tell.tellType)},${tell.bodyPart},${tell.confidence},"${escapeCSV(tell.description)}","${escapeCSV(tell.recommendation || '')}"`
    );
  });

  // Pitch analysis rows
  jsonData.pitchByPitch.forEach((pitch) => {
    const indicators = pitch.indicators.join('; ');
    rows.push(
      `PITCH,${pitch.pitchNumber},${pitch.pitchType},Pitch Analysis,Mechanics,${pitch.confidence},"${escapeCSV(indicators)}",""`
    );
  });

  // Recommendation rows
  jsonData.recommendations.forEach((rec, idx) => {
    rows.push(`RECOMMENDATION,${idx + 1},,Mechanical Fix,,,"${escapeCSV(rec)}",""`);
  });

  return rows.join('\n');
}

/**
 * Escape string for CSV
 * @param {string} str - String to escape
 * @returns {string} Escaped string
 */
function escapeCSV(str) {
  if (!str) return '';
  // Escape double quotes and wrap in quotes if needed
  return str.replace(/"/g, '""').replace(/\n/g, ' ');
}
